import { NOTIFICATION_ACTIONS_DISPLAY_LABELS, NOTIFICATION_READ_DELAY_MILLISECONDS } from '@/constants';
import useReadNotification from '@/hooks/notifications/useReadNotification';
import { TNotification } from '@/types/Notification.types';
import { HStack, VStack } from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import NotificationItemDisplay from './NotificationItemDisplay';
import { useNavigate } from '@tanstack/react-router';
import { useAppStore } from '@/store/useAppStore';
import roles from '@/constants/roles';

interface Props {
  notification: TNotification;
  onViewed?: (notification: TNotification) => void;
  onCTAed?: () => void;
}

const getNotificationMetadataValue = (notification: TNotification, key: string) => {
  const metadata = notification.metadata as Record<string, unknown> | undefined;
  const value = metadata?.[key];

  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const NotificationListItem = ({ notification, onViewed, onCTAed }: Props) => {
  const navigate = useNavigate();

  const { user } = useAppStore();

  const hasCTA = !!notification.action;

  const isNotificationRead = notification.isRead;

  const isPeerSupport = user?.roles.includes(roles.peerSupport);
  const notificationActionIsForPeerSupport = notification.action?.includes('PEER_SUPPORT');
  const notificationContentId = getNotificationMetadataValue(notification, 'contentId');
  const needsContentId = notification.action === 'VIEW_CONTENT' || notification.action === 'EDIT_CONTENT_DRAFT';
  const hasHandleableCTA = !needsContentId || !!notificationContentId;

  const { mutate: readNotification } = useReadNotification(notification);

  const { ref: inViewRef, inView } = useInView({
    threshold: 0.9,
    trackVisibility: !!onViewed,
    skip: !onViewed,
    delay: 300,
  });

  useEffect(() => {
    if (!!onViewed && !isNotificationRead && !hasCTA && inView) {
      setTimeout(
        () => readNotification({ notificationId: notification._id, onSuccessCallback: onViewed }),
        NOTIFICATION_READ_DELAY_MILLISECONDS,
      );
    }
  }, [onViewed, isNotificationRead, hasCTA, inView]);

  const onCTAClick = useCallback(async () => {
    await readNotification({ notificationId: notification._id, onSuccessCallback: onCTAed });

    switch (notification.action) {
      case 'SCHEDULE_APPOINTMENT':
        return navigate({ to: '/directory/provider' });

      case 'SCREENER_CHECK_IN':
        return navigate({ to: `/screener/${notification.metadata?.screenerSlug}` });

      case 'VIEW_PROFILE':
        return navigate({ to: '/user/profile' });

      case 'VIEW_CONTENT':
        if (!notificationContentId) return undefined;

        // The loop is the universal content target: it handles all three types, whereas
        // /article/$contentId only suits articles and a notification does not carry the
        // content type.
        return navigate({ to: '/loop/$contentId', params: { contentId: notificationContentId } });

      case 'UPGRADE_USER_TO_PEER_SUPPORT':
        return navigate({ to: '/organization/dashboard/peer-support' });

      case 'PEER_SUPPORT_REQUEST':
        return navigate({ to: '/organization/dashboard/peer-support' });

      case 'UPGRADE_USER_TO_PEER_SUPPORT_LEAD':
        return navigate({ to: '/organization/dashboard/peer-support' });

      case 'EDIT_CONTENT_DRAFT':
        if (!notificationContentId) return undefined;

        return navigate({ to: '/creator/content/$contentId', params: { contentId: notificationContentId } });

      case undefined:
      default:
        return undefined;
    }
  }, [navigate, notification, notificationContentId, onCTAed, readNotification]);

  return (
    <VStack ref={inViewRef}>
      <NotificationItemDisplay
        title={notification.title.toUpperCase()}
        timeStamp={formatDistanceToNow(notification.createdAt, { addSuffix: true })}
        message={notification.message}
        isRead={isNotificationRead}
        CTALabel={
          !notification.action || !hasHandleableCTA || (notificationActionIsForPeerSupport && !isPeerSupport)
            ? undefined
            : NOTIFICATION_ACTIONS_DISPLAY_LABELS[notification.action]
        }
        onCTAClick={
          !notification.action || !hasHandleableCTA || (notificationActionIsForPeerSupport && !isPeerSupport)
            ? undefined
            : onCTAClick
        }
      />

      <HStack height={'1px'} backgroundColor={'#33516D24'} width={'114%'} />
    </VStack>
  );
};

export default NotificationListItem;
